import React, { useState, useEffect, useId, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import { faQuestionCircle, faRightToBracket } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { selectUser } from '../../../redux/userSlice'
import { selectAuth } from '../../../redux/authSlice'
import { selectModules } from '../../../redux/moduleSlice'
import { setNotification } from '../../../redux/notificationSlice'
import { hasUserPermission } from '../../../utils/permissionHelper'
import LayoutSidebar from '../../../components/LayoutSidebar'
import Loading from '../../../components/Loading'
import TableHeader from '../../../components/TableHeader'
import TableRow from '../../../components/TableRow'
import Pagination from '../../../components/Pagination'
import { useForm, Controller } from 'react-hook-form'
import { useRequest } from '../../../hooks/UseRequest'
import { selectDropdownOptions } from '../../../redux/dropdownSlice'
import { selectMultiSelectedRows, setSelectedRows } from '../../../redux/multiSelectRowSlice'
import { generateArrayOfErrorsToBody } from '../../../helpers/errorHandling'
import Select from 'react-select'
import { reactSelectClassNames } from '../../../helpers/react-select-styles'

export default function ModuleQueue(props) {
  const navi = useNavigate()
  const startId = useId()
  const dispatch = useDispatch()
  const params = useParams()

  // Redux Selectors
  const modules = useSelector(selectModules)
  const active = useSelector(selectUser)
  const requests = useRequest()
  const dropdownOptions = useSelector(selectDropdownOptions);
  const auth = useSelector(selectAuth)

  // Local State
  const [module, setModule] = useState()
  const [moduleCount, setModuleCount] = useState(-1)
  const [loaded, setLoaded] = useState(true)
  const [partialLoad, setPartialLoad] = useState(false)
  const [searchFilter, setSearchFilter] = useState('')
  const [urlFilter, setUrlFilter] = useState('')
  const [validPortfolios, setValidPortfolios] = useState([]);
  const [userPermissions, setUserPermissions] = useState()
  const [portfolioId, setPortfolioId] = useState(null);
  const { control, setValue } = useForm();
  const [moduleData, setModuleData] = useState([])
  const [moduleMeta, setModuleMeta] = useState([])
  const [moduleFields, setModuleFields] = useState(false)
  const [moduleQueueName, setModuleQueueName] = useState()
  const multiSelectedRows = useSelector(selectMultiSelectedRows)
  const [multiSelectCompleteButtonText, setMultiSelectCompleteButtonText] = useState('');
  const submittingQueueItems = useRef(false);
  const [queueStatus, setQueueStatus] = useState(null);

  const validQueueStatus = [
    { label: "Pending", value: 'pending' },
    { label: "Completed", value: 'completed' }
  ]

  useEffect(() => {
    if (active.status === 'succeeded' && module) {
      const activePermissions = active.spoofed_user.user_permission_options ? active.spoofed_user.user_permission_options : active.data.user_permission_options
      setUserPermissions(activePermissions)
      if (!hasUserPermission(activePermissions, `${module.code_key}_read`)) {
        navi('/403')
      }
    }
  }, [active, module])

  useEffect(() => {
    if (params.module && params.action_type && auth.accessToken) {
      getQueueFieldsData()
    }
  }, [params.module, params.action_type, auth.accessToken])

  useEffect(() => {
    const numberOfRecords = multiSelectedRows.isAll ? (moduleData?.data?.length ?? 0) : (multiSelectedRows?.selectedRows?.length ?? 0);
    const buttonText = `Complete ${numberOfRecords <= 0 ? '' : numberOfRecords} Queue Item${numberOfRecords <= 1 ? '' : 's'}`
    setMultiSelectCompleteButtonText(buttonText);
  }, [multiSelectedRows.selectedRows, multiSelectedRows.isAll])

  const getQueueData = async (url) => {
    try {
      dispatch(setSelectedRows({ selectedRows: [], isAll: false }))
      setPartialLoad(false)
      requests.setEndpoint(url)
      const response = await requests.get()
      let { data, ...metaData } = response.data
      setModuleData(response.data)
      setModuleMeta(metaData)
      setPartialLoad(true)
    } catch (error) {
      setPartialLoad(true)
      dispatch(setNotification({ type: 'error', body: error.response?.data?.message }))
    }
  }

  const getQueueFieldsData = async () => {
    try {
      const m = modules.data.find((e) => e.code_key === params.module);

      setModuleQueueName(dropdownOptions.find((o) => o?.code_key === params.action_type)?.name)
      if (m) {
        setModule(m);
        if (m?.code_key && params.action_type) {
          const url = `${process.env.REACT_APP_API_DOMAIN}/get_module_queue_fields/${m.code_key}/${params.action_type}`
          requests.setEndpoint(url)
          const response = await requests.get()
          const filterModuleFields = response.data.data
            .filter((mf) => {
              return mf.is_show_in_list_view === 1
            })
            .sort((m1, m2) => m1.list_display_rank - m2.list_display_rank)
          setModuleFields(filterModuleFields);
          setValue('queue_status', validQueueStatus[0])
          setQueueStatus('pending')
        }
      } else {
        navi('/404');
      }
    } catch (error) {
      dispatch(setNotification({ type: 'error', body: error.response?.data?.message }))
    }
  }

  const fetchData = async () => {
    if (module?.code_key && params.action_type && moduleFields && queueStatus) {
      const url = `${process.env.REACT_APP_API_DOMAIN}/get_module_queue/${module.code_key}/${params.action_type}?queue_status=${queueStatus}`
      getQueueData(url)
    }
  }

  const completeQueueItems = async () => {
    try {
      if (submittingQueueItems.current) return;
      setPartialLoad(false);
      setMultiSelectCompleteButtonText("Completing Queue(s)...");
      submittingQueueItems.current = true;
      let recordIds = [];
      if (multiSelectedRows.isAll) {
        recordIds = moduleData.data.map((x) => x.id);
      } else {
        recordIds = multiSelectedRows.selectedRows;
      }
      const formData = {
        record_ids: recordIds,
        module_id: module.id,
        action_type_dropdown_option_id: dropdownOptions.find((o) => o?.code_key === params.action_type)?.id
      }
      requests.setEndpoint(
        `${process.env.REACT_APP_API_DOMAIN}/module_queue/complete_queue_items`
      )
      const response = await requests.post(formData)
      if (response.status === 200) {
        dispatch(setSelectedRows({ selectedRows: [], isAll: false }))
        submittingQueueItems.current = false;
        await fetchData();
        dispatch(setNotification({ type: 'success', body: response?.data?.message }))
      }
    } catch (error) {
      let body = 'An error happened'
      if (error?.response?.data?.message) {
        body = error?.response?.data?.message
      } else if (Array.isArray(error?.response?.data?.errors)) {
        body = generateArrayOfErrorsToBody(error.response.data.errors)
      }
      submittingQueueItems.current = false;
      dispatch(setNotification({ type: 'error', body: body }))
      dispatch(setSelectedRows({ selectedRows: [], isAll: false }))
      setPartialLoad(true);
    }
  }

  useEffect(() => {
    if (auth.accessToken && moduleFields && queueStatus) {
      fetchData()
    }
  }, [moduleFields, params.action_type, auth.accessToken, queueStatus])

  /**
   * After the API call for module data succeeds stop loading and set the record count
   */
  useEffect(() => {
    if (moduleData && moduleData?.meta?.total) {
      setModuleCount(moduleData.meta.total)
    } else {
      setModuleCount(moduleData.data && moduleData.data.length)
    }
  }, [moduleData])

  if (module) {
    const count = 0

    const contentTitle = (moduleCount !== -1) ? (
      <>
        <span>
          Module Queue: {moduleQueueName}
        </span>
        <span>
          ({moduleCount})
        </span>
      </>
    ) : (
      <>
        <span>
           Module Queue: {moduleQueueName}
        </span>
        <Loading size="is-size-4" />
      </>
    )

    return (
      <LayoutSidebar
        contentTitle={contentTitle}
        content={((loaded || partialLoad) && moduleFields) ? (
          <div className="has-pagination pt-3 mb-5">

            <div
              className={`${moduleData.data?.length <= 3 && 'lox-hooktable-dataless'} lox-hooktable table-container mb-0 position-relative`}
            >
              {!partialLoad && (
                <div className="loader-wrapper">
                  <Loading />
                </div>
              )}
              <div className="columns m-2">
                <div className="field is-horizontal">
                  <label htmlFor="queue_status" style={{ width: '150px' }}>Queue Status:</label>
                  <div className="control" style={{ width: '300px' }}>
                    <Controller
                      name="queue_status"
                      control={control}
                      render={({ field }) => (
                        <Select
                          {...field}
                          isSearchable={false}
                          classNames={reactSelectClassNames()}
                          options={validQueueStatus}
                          onChange={(val) => {
                            setQueueStatus(val?.value)
                            field.onChange({ label: val?.label, value: val?.value })
                          }}
                          menuPosition="fixed"
                          styles={{ menu: ({ ...css }) => ({ ...css, width: '100%' }) }}
                        />
                      )}
                    />
                  </div>
                </div>
              </div>
              {queueStatus === 'pending' && (
                <div className="level mb-0 py-2">
                  <div className="field is-12" style={{ width: '100%' }}>
                    <button
                      className="button is-primary is-small px-2 mr-1"
                      disabled={((multiSelectedRows.isAll ? (moduleCount === 0) : (multiSelectedRows?.selectedRows?.length ?? 0)) === 0) || submittingQueueItems.current}
                      onClick={() => completeQueueItems()}
                    >
                      {multiSelectCompleteButtonText}
                    </button>
                  </div>
                </div>
              )}

              <div className="list-table-container">
                <table
                  className="lox-hooktable-table table is-striped is-narrow is-hoverable is-fullwidth is-info has-sticky-column has-view-button"
                >
                  <TableHeader
                    columns={moduleFields}
                    startId={startId}
                    count={count}
                    hasActions={false}
                    hasView
                    setPartialLoad={setPartialLoad}
                    url={module.code_key + urlFilter}
                    searchFilter={searchFilter}
                    module={module}
                    hasMultiSelect={queueStatus === 'pending'}
                  />
                  {moduleData?.data?.length === 0 && (
                    <tbody>
                    <tr>
                      <td
                        className="is-size-6 is-italic"
                        style={{ padding: "20px", color: "grey" }}
                        colSpan={moduleFields.length}
                      >No Record(s) Found
                      </td>
                    </tr>
                    </tbody>
                  )}
                  <tbody>
                  <TableRow
                    rows={moduleData.data}
                    columns={moduleFields}
                    hasActions={false}
                    hasView
                    baseName={module.code_key}
                    setPartialLoad={setPartialLoad}
                    hasMultiSelect={queueStatus === 'pending'}
                    parentModuleCodeKey={'module_queue_general'}
                  />
                  </tbody>
                </table>
              </div>
            </div>
            {/* If there is more than one page, show pagination */}
            {moduleMeta && Object.keys(moduleMeta).length > 0 && moduleMeta?.last_page !== 1 && (
              <Pagination
                meta={moduleMeta}
                setPartialLoad={() => {
                }}
                isCustomCall
                setDataFunction={getQueueData}
              />
            )}
          </div>
        ) : <Loading />}
      />
    )
  }
}
